/**
 * Ops Center page (PRD §5.8 S-03).
 *
 * Mission-control layout. Three swappable views in the main left panel
 * (Map / Schematic / Waterfall), with a constant right column
 * (AlertFeed) and bottom strip (KpiBar). Click any pin/node/cell/feed
 * row → loads incident detail in the side panel.
 *
 * Wires together everything built across days 6–8.5:
 *   - CorridorMap     (S-01 / U-01)
 *   - SchematicCorridor (S-07)
 *   - WaterfallDiagram (S-06)
 *   - KpiBar          (S-05)
 *   - AlertFeed       (S-03)
 *   - useAlertStream  (U-02 SSE)
 *
 * SSE handling: a single useAlertStream subscription is shared across
 * all four panels; on every event we invalidate the relevant TanStack
 * Query keys instead of pushing optimistic updates. Refetches are
 * cheap (incidents endpoint < 50ms; KPI cached 30s).
 *
 * Keyboard shortcuts: 1/2/3 swap views. We bind on window but skip the
 * shortcut when the active element is an input/textarea/select or
 * contenteditable — operators must be able to type incident notes
 * without accidentally swapping views.
 */

import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "wouter";
import { useTranslation } from "react-i18next";
import { Map as MapIcon, ListTree } from "lucide-react";
import { api, fetchKpiShift, fetchWaterfall } from "@/lib/api";
import type { ShiftKpi, WaterfallApiData } from "@/lib/api";
import { useAlertStream } from "@/lib/useAlertStream";
import {
  OpsViewToggle,
  loadStoredOpsView,
  persistOpsView,
  OPS_VIEW_KEYS,
  type OpsView,
} from "@/components/OpsViewToggle";
import { CorridorMap } from "@/components/map/CorridorMap";
import { SchematicCorridor } from "@/components/SchematicCorridor";
import { WaterfallDiagram } from "@/components/WaterfallDiagram";
import { AlertFeed } from "@/components/AlertFeed";
import { KpiBar } from "@/components/KpiBar";
import type { IncidentDto } from "@/lib/types";

interface SitesResponse {
  sites: Array<{
    id: string;
    siteId: string;
    name: string;
    centroidLat: number | null;
    centroidLng: number | null;
    metadata: Record<string, unknown>;
  }>;
}

interface OpsOverview {
  site: {
    id: string;
    siteId: string;
    name: string;
    centroidLat: number | null;
    centroidLng: number | null;
    metadata: Record<string, unknown>;
  };
  segments: Array<{
    id: string;
    segmentId: string;
    name: string | null;
    startChainage: number;
    endChainage: number;
    centroidLat: number | null;
    centroidLng: number | null;
    gisPolyline: { type: "LineString"; coordinates: [number, number][] } | null;
  }>;
  landmarks: { valveKps: number[]; scraperKps: number[] };
}

interface IncidentList {
  incidents: IncidentDto[];
  pagination: { limit: number; offset: number; count: number };
}

const DEFAULT_SITE_ID = "PGU_SOUTHERN_BRANCH";

export function OpsCenterPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const [view, setView] = useState<OpsView>(() => loadStoredOpsView());
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(
    null,
  );
  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null,
  );

  // ── Data fetches ────────────────────────────────────────────────────
  const sitesQuery = useQuery<SitesResponse>({
    queryKey: ["sites"],
    queryFn: () => api<SitesResponse>("/assets/sites"),
    staleTime: 60_000,
  });

  // Pick the pilot site (PGU_SOUTHERN_BRANCH) by code, falling back to
  // the first site if not present (dev environments).
  const site = useMemo(() => {
    const list = sitesQuery.data?.sites ?? [];
    return (
      list.find((s) => s.siteId === DEFAULT_SITE_ID) ?? list[0] ?? null
    );
  }, [sitesQuery.data]);

  const overviewQuery = useQuery<OpsOverview>({
    queryKey: ["ops-overview", site?.id],
    queryFn: () => api<OpsOverview>(`/assets/sites/${site!.id}/ops-overview`),
    enabled: Boolean(site?.id),
    staleTime: 60_000,
  });

  const incidentsQuery = useQuery<IncidentList>({
    queryKey: ["incidents", "ops-center"],
    queryFn: () => api<IncidentList>("/incidents?limit=50"),
    refetchInterval: 5_000,
  });

  const kpiQuery = useQuery<ShiftKpi>({
    queryKey: ["kpi-shift", site?.siteId],
    queryFn: () => fetchKpiShift(site!.siteId),
    enabled: Boolean(site?.siteId),
    refetchInterval: 10_000,
  });

  const waterfallQuery = useQuery<WaterfallApiData>({
    queryKey: ["waterfall", site?.siteId],
    queryFn: () =>
      fetchWaterfall(site!.siteId, {
        windowSeconds: 600,
        bucketSeconds: 30,
      }),
    enabled: Boolean(site?.siteId) && view === "waterfall",
    refetchInterval: view === "waterfall" ? 15_000 : false,
  });

  // ── SSE: refresh on every incident event ────────────────────────────
  const stream = useAlertStream({ siteId: site?.siteId ?? null });
  useEffect(() => {
    if (!stream.lastEvent) return;
    if (stream.lastEvent.type === "keepalive") return;
    void qc.invalidateQueries({ queryKey: ["incidents"] });
    void qc.invalidateQueries({ queryKey: ["kpi-shift"] });
    if (view === "waterfall") {
      void qc.invalidateQueries({ queryKey: ["waterfall"] });
    }
    // refetch the currently-loaded incident detail too
    if (selectedIncidentId) {
      void qc.invalidateQueries({
        queryKey: ["incident", selectedIncidentId],
      });
    }
  }, [stream.eventCount, qc, view, selectedIncidentId, stream.lastEvent]);

  // ── Keyboard shortcuts (1 / 2 / 3) ──────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        target?.isContentEditable
      ) {
        return;
      }
      const match = OPS_VIEW_KEYS.find((k) => k.key === e.key);
      if (!match) return;
      e.preventDefault();
      changeView(match.view);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function changeView(next: OpsView) {
    setView(next);
    persistOpsView(next);
  }

  // ── Derived data ────────────────────────────────────────────────────
  const incidents = incidentsQuery.data?.incidents ?? [];
  const segments = overviewQuery.data?.segments ?? [];
  const landmarks = overviewQuery.data?.landmarks;
  const center = site
    ? {
        lat: site.centroidLat ?? 1.95,
        lng: site.centroidLng ?? 103.4,
      }
    : { lat: 1.95, lng: 103.4 };

  // Only OPEN incidents get pins on the map / colour the schematic.
  const openIncidents = incidents.filter(
    (i) => i.status !== "RESOLVED",
  );

  const mapAlerts = useMemo(() => {
    return openIncidents
      .map((i) => {
        const seg = segments.find((s) => s.segmentId === i.segmentId);
        if (!seg || seg.centroidLat == null || seg.centroidLng == null) {
          return null;
        }
        return {
          incident_id: i.id,
          segment_id: i.segmentId,
          event_type: i.eventType,
          confidence: i.confidence,
          status: i.status,
          centroid_lat: seg.centroidLat,
          centroid_lng: seg.centroidLng,
        };
      })
      .filter((x): x is NonNullable<typeof x> => x !== null);
  }, [openIncidents, segments]);

  const mapSegments = useMemo(() => {
    return segments
      .filter((s) => s.centroidLat != null && s.centroidLng != null)
      .map((s) => ({
        segment_id: s.segmentId,
        centroid_lat: s.centroidLat as number,
        centroid_lng: s.centroidLng as number,
        gis_polyline: s.gisPolyline,
      }));
  }, [segments]);

  // ── Click handlers ──────────────────────────────────────────────────
  function onIncidentSelect(id: string) {
    setSelectedIncidentId(id);
    // surface segment selection too so the schematic ring highlights
    const inc = incidents.find((i) => i.id === id);
    if (inc) setSelectedSegmentId(inc.segmentId);
  }

  function onSegmentClick(segmentId: string) {
    setSelectedSegmentId(segmentId);
    // load most recent open incident on that segment, if any
    const match = openIncidents.find((i) => i.segmentId === segmentId);
    if (match) setSelectedIncidentId(match.id);
  }

  function onCellClick(segmentId: string) {
    onSegmentClick(segmentId);
  }

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="space-y-3" data-testid="ops-center-page">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <ListTree className="size-5 text-teal-400" />
            {t("opsCenter.title", { defaultValue: "Ops Center" })}
          </h1>
          <p className="text-xs text-slate-400">
            {t("opsCenter.subtitle", {
              defaultValue:
                "Live corridor view — Map / Schematic / Waterfall. Press 1, 2, 3 to switch.",
            })}
            {site && (
              <span className="ml-2 font-mono text-slate-500">
                · {site.siteId}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span
            className="text-[10px] uppercase tracking-wider text-slate-500"
            data-testid="sse-status"
            data-sse-connected={stream.connected ? "true" : "false"}
          >
            SSE {stream.connected ? "live" : "offline"}
          </span>
          <OpsViewToggle value={view} onChange={changeView} />
          <Link
            href="/app"
            className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1"
            data-testid="ops-back-to-dashboard"
          >
            <MapIcon className="size-3" />
            {t("opsCenter.backToDashboard", {
              defaultValue: "Classic dashboard",
            })}
          </Link>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_360px] gap-3">
        {/* Main view */}
        <div className="space-y-3">
          <div data-testid={`ops-active-view-${view}`} data-active-view={view}>
            {view === "map" && (
              <CorridorMap
                centerLat={center.lat}
                centerLng={center.lng}
                zoom={9}
                segments={mapSegments}
                alerts={mapAlerts}
                onAlertClick={onIncidentSelect}
              />
            )}
            {view === "schematic" && (
              <SchematicCorridor
                segments={segments.map((s) => ({
                  segmentId: s.segmentId,
                  startChainage: s.startChainage,
                  name: s.name,
                }))}
                incidents={openIncidents.map((i) => ({
                  id: i.id,
                  segmentId: i.segmentId,
                  status: i.status,
                }))}
                landmarks={landmarks}
                onSegmentClick={onSegmentClick}
                selectedSegmentId={selectedSegmentId}
              />
            )}
            {view === "waterfall" && waterfallQuery.data && (
              <WaterfallDiagram
                data={waterfallQuery.data}
                onCellClick={onCellClick}
              />
            )}
            {view === "waterfall" && !waterfallQuery.data && (
              <section
                className="rounded-lg border border-slate-800 bg-slate-900/40 p-6 text-xs text-slate-500"
                data-testid="waterfall-loading"
              >
                {waterfallQuery.isLoading
                  ? t("opsCenter.waterfallLoading", {
                      defaultValue: "Loading waterfall…",
                    })
                  : t("opsCenter.waterfallEmpty", {
                      defaultValue:
                        "Waterfall awaiting site signal. No data in the last 10 minutes.",
                    })}
              </section>
            )}
          </div>

          {/* KPI bar — full width below the active view */}
          {kpiQuery.data ? (
            <KpiBar data={kpiQuery.data} />
          ) : (
            <div
              className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-500"
              data-testid="kpi-bar-loading"
            >
              {t("opsCenter.kpiLoading", { defaultValue: "Loading KPIs…" })}
            </div>
          )}
        </div>

        {/* Side column: alert feed + selected incident summary */}
        <aside className="space-y-3">
          <AlertFeed
            incidents={incidents}
            onIncidentClick={onIncidentSelect}
            selectedIncidentId={selectedIncidentId}
            loading={incidentsQuery.isLoading}
            maxRows={20}
          />
          <SelectedIncidentSummary incidentId={selectedIncidentId} />
        </aside>
      </div>
    </div>
  );
}

/**
 * Compact, read-only summary of the currently-selected incident.
 * Full action bar + audit lives on /incidents/:id — this is just enough
 * for an operator to decide whether to drill in.
 */
function SelectedIncidentSummary({ incidentId }: { incidentId: string | null }) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<{ incident: IncidentDto }>({
    queryKey: ["incident", incidentId],
    queryFn: () => api(`/incidents/${incidentId}`),
    enabled: Boolean(incidentId),
    refetchInterval: 5_000,
  });

  if (!incidentId) {
    return (
      <section
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-xs text-slate-500 italic"
        data-testid="ops-incident-summary-empty"
      >
        {t("opsCenter.noIncidentSelected", {
          defaultValue:
            "Click a pin, node, cell, or feed row to load an incident.",
        })}
      </section>
    );
  }
  if (isLoading || !data) {
    return (
      <section
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4 text-xs text-slate-400"
        data-testid="ops-incident-summary-loading"
      >
        Loading incident…
      </section>
    );
  }

  const incident = data.incident;
  return (
    <section
      className="rounded-lg border border-slate-800 bg-slate-900/40 p-3 space-y-2"
      data-testid="ops-incident-summary"
      data-incident-id={incident.id}
    >
      <header className="flex items-center justify-between gap-2">
        <span className="text-xs uppercase tracking-wide text-slate-300 font-semibold">
          {incident.eventType}
        </span>
        <span
          className={`text-base font-mono ${
            incident.confidence >= 0.8
              ? "text-emerald-400"
              : incident.confidence >= 0.5
              ? "text-amber-300"
              : "text-rose-400"
          }`}
        >
          {Math.round(incident.confidence * 100)}%
        </span>
      </header>
      <div className="text-xs text-slate-400 font-mono">
        {incident.siteId} / {incident.segmentId}
      </div>
      <p className="text-xs text-slate-300 line-clamp-3">
        {incident.reasoning}
      </p>
      <Link
        href={`/incidents/${incident.id}`}
        className="inline-block text-xs text-teal-300 hover:text-teal-200"
        data-testid="ops-incident-open-detail"
      >
        Open full detail →
      </Link>
    </section>
  );
}
